import { FLAGS } from '@iheartjane/feature-flags';
import { BannerCarousel } from '@iheartjane/menu';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CarouselSkeleton } from '@jane/bloom/components/skeletonLoaders/carouselSkeleton';
import { useIsNavigatingAway } from '@jane/bloom/hooks/navigation/useIsNavigating';
import { usePinStore } from '@jane/bloom/hooks/storeSwitcher/usePinStore';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { CarouselBanner } from '@jane/bloom/types/schemas/roots/carouselBanner';

import styles from './bloomCarousel.module.css';
import { CarouselSwiper } from './carouselSwiper/carouselSwiper';

interface BloomCarouselProps {
  items: CarouselBanner;
  stockPhoto?: boolean;
  storeName?: string;
  variant?: 'brand' | 'product';
  withLightBox?: boolean;
}

const AUTO_SLIDE_INTERVAL = 7000;

export const BloomCarousel = ({
  items,
  storeName,
  variant,
}: BloomCarouselProps) => {
  const { featureFlags } = useRootLoaderData();
  const isRedesign = !!featureFlags[FLAGS.c2CarouselRedesign];

  const { pinning: manuallyPinning } = usePinStore();
  const isNavigatingAway = useIsNavigatingAway();
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const shouldShowSkeleton = manuallyPinning || isNavigatingAway;

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // redesign owns its auto-advance
    if (isRedesign) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % items.length);
    }, AUTO_SLIDE_INTERVAL);
  }, [isRedesign, items.length]);

  const updateIndex = (newIndex: number) => {
    setActiveIndex(newIndex);
    startAutoSlide(); // reset interval when index is manually updated
  };

  useEffect(() => {
    startAutoSlide();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startAutoSlide]);

  if (shouldShowSkeleton) {
    return <CarouselSkeleton mode="carousel" />;
  }

  if (isRedesign) {
    const slides = items.map((banner) => ({
      imageAlt: banner.alt_text ?? `${storeName} Marketing Banner`,
      imageSrc: banner.image_url,
      url: banner.url,
    }));

    return (
      <div
        className={styles['redesignCarousel']}
        data-testid="banner-carousel-redesign"
      >
        <BannerCarousel slides={slides} autoAdvanceMs={AUTO_SLIDE_INTERVAL} />
      </div>
    );
  }

  return (
    <div data-testid="banner-carousel-legacy">
      <CarouselSwiper
        items={items}
        activeIndex={activeIndex}
        updateIndex={updateIndex}
        variant={variant}
        storeName={storeName}
      />
    </div>
  );
};
